/**
 * Song page top bar: back, title, setlist prev/next (lg+), "Æfing dagsins" (the practice's songs),
 * Nótur|Texti, "Útlit síðu" (Samfellt |
 * Heil síða | Tvær síður — from 640 px, Nótur tab), "Allar skrár" (editors), player toggle, the big
 * "Skrifa" edit toggle (Nótur tab); under it the setlist controls with labels below lg (phones, iPad
 * portrait — the main row has no room there). The strip of open songs ("Opin lög") floats over the
 * sheet instead (tabs/TabStrip). Labels give way to icons on narrower screens so a portrait iPad
 * fits the layout switch: Back's from lg, "Allar skrár"'s from xl. Reading mode (readingMode.ts):
 * "Fela stikur" folds the bars into one floating button; shown again over the sheet, the bar offers
 * "Festa stikur" (keep them) and ⌃ (fold them again).
 */
import { Check, ChevronLeft, ChevronUp, FileMusic, Files, Headphones, Maximize, PenLine, Pin, Text } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAudioMaybe } from '../audio/AudioProvider';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { PageModeToggle } from './PageModeToggle';
import { SetlistNav } from './SetlistNav';
import type { SetlistNavigation } from './useSetlist';

export type SongTab = 'sheet' | 'lyrics';

function Segmented({ tab, onChange }: { tab: SongTab; onChange: (t: SongTab) => void }) {
  const t = useT();
  const items: { id: SongTab; label: string; icon: typeof FileMusic }[] = [
    { id: 'sheet', label: t('viewer.tab_sheet'), icon: FileMusic },
    { id: 'lyrics', label: t('viewer.tab_lyrics'), icon: Text },
  ];
  return (
    <div role="tablist" aria-label={t('viewer.tabs_label')} className="flex shrink-0 rounded-xl bg-[var(--surface-2)] p-1 ring-1 ring-[var(--line)]">
      {items.map((it) => {
        const on = tab === it.id;
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(it.id)}
            className={cx(
              'inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-[16px] font-semibold transition',
              on ? 'bg-[var(--surface)] text-[var(--accent)] shadow-sm' : 'text-[var(--muted)]',
            )}
          >
            <it.icon className="hidden size-5 sm:block" aria-hidden />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export function SongTopBar({
  title,
  subtitle,
  onBack,
  tab,
  onTab,
  showTabs,
  allFiles,
  onAllFiles,
  showAllFilesToggle,
  dockOpen,
  onDock,
  showDockToggle,
  edit,
  extra,
  pageMode,
  setlist,
  onShowList,
  practice,
  reading,
}: {
  title: string;
  subtitle?: string | null;
  onBack: () => void;
  tab: SongTab;
  onTab: (t: SongTab) => void;
  showTabs: boolean;
  allFiles: boolean;
  onAllFiles: (v: boolean) => void;
  showAllFilesToggle: boolean;
  dockOpen: boolean;
  onDock: (v: boolean) => void;
  showDockToggle: boolean;
  /** "Skrifa" ↔ "Lokið" toggle between view and edit mode (only on the Nótur tab). */
  edit?: { editing: boolean; onToggle: () => void } | null;
  /** Extra actions (e.g. the save-for-offline button), shown before the edit toggle. */
  extra?: ReactNode;
  /** "Útlit síðu" (Nótur tab with a sheet); "Tvær síður" only while the viewer is wide enough for it. */
  pageMode?: { twoUpEligible: boolean } | null;
  /** Setlist prev/next: compact in the main row from lg up, in its own labelled row below lg. */
  setlist?: SetlistNavigation | null;
  /** Opens the list's songs from the setlist's "3 / 12". */
  onShowList?: () => void;
  /** "Æfing dagsins" quick menu (practice/PracticeMenu; renders nothing without a practice). */
  practice?: ReactNode;
  /** Reading mode: fold the bars away, or — shown over the sheet — keep them / fold them again. */
  reading?: { shownOver: false; onFold: () => void } | { shownOver: true; onPin: () => void; onClose: () => void } | null;
}) {
  const t = useT();
  const audio = useAudioMaybe();
  const playingHidden = !!audio?.playing && !dockOpen;
  return (
    <header
      className="z-20 shrink-0 border-b border-[var(--line)] bg-[var(--surface)]"
      style={{ paddingTop: 'var(--top-inset)', paddingLeft: 'var(--safe-left)', paddingRight: 'var(--safe-right)' }}
    >
      <div className="flex min-h-14 items-center gap-1.5 px-2 sm:gap-2 sm:px-3">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('common.back')}
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-0.5 rounded-xl font-semibold text-[var(--accent)] hover:bg-[var(--accent-soft)] lg:pr-3"
        >
          <ChevronLeft className="size-7" aria-hidden />
          <span className="hidden lg:inline">{t('common.back')}</span>
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[19px] leading-tight font-bold sm:text-[21px]">{title}</h1>
          {subtitle ? <p className="hidden truncate text-[14px] text-[var(--muted)] sm:block">{subtitle}</p> : null}
        </div>
        {setlist ? (
          <div className="hidden shrink-0 lg:flex">
            <SetlistNav nav={setlist} onShowList={onShowList} />
          </div>
        ) : null}
        {practice}
        {showTabs ? <Segmented tab={tab} onChange={onTab} /> : null}
        {pageMode ? <PageModeToggle twoUpEligible={pageMode.twoUpEligible} /> : null}
        {showAllFilesToggle ? (
          <button
            type="button"
            aria-pressed={allFiles}
            onClick={() => onAllFiles(!allFiles)}
            title={t('viewer.all_files_hint')}
            className={cx(
              'inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-xl px-2 text-[15px] font-semibold transition sm:px-3',
              allFiles ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'text-[var(--ink-2)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]',
            )}
          >
            <Files className="size-5" aria-hidden />
            <span className="hidden xl:inline">{t('viewer.all_files')}</span>
            <span className="sr-only xl:hidden">{t('viewer.all_files')}</span>
          </button>
        ) : null}
        {showDockToggle ? (
          <button
            type="button"
            aria-pressed={dockOpen}
            onClick={() => onDock(!dockOpen)}
            aria-label={dockOpen ? t('viewer.audio_hide') : t('viewer.audio_show')}
            title={dockOpen ? t('viewer.audio_hide') : t('viewer.audio_show')}
            className={cx(
              'relative inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-xl px-2 text-[15px] font-semibold transition sm:px-3',
              dockOpen ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'text-[var(--ink-2)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]',
            )}
          >
            <Headphones className="size-5" aria-hidden />
            {playingHidden ? (
              <span className="absolute top-1.5 right-1.5 size-2.5 animate-pulse rounded-full bg-[var(--ok)]" aria-hidden />
            ) : null}
            <span className="hidden lg:inline">{t('viewer.audio_short')}</span>
          </button>
        ) : null}
        {reading && !reading.shownOver ? (
          <button
            type="button"
            onClick={reading.onFold}
            aria-label={t('viewer.bars_fold')}
            title={t('viewer.bars_fold_hint')}
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-[var(--ink-2)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]"
          >
            <Maximize className="size-5" aria-hidden />
          </button>
        ) : null}
        {extra}
        {edit ? (
          <button
            type="button"
            aria-pressed={edit.editing}
            onClick={edit.onToggle}
            title={edit.editing ? t('annotate.done_hint') : t('annotate.write_hint')}
            className={cx(
              'inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl px-3 text-[16px] font-bold shadow-sm transition active:scale-95 sm:px-4 sm:text-[17px]',
              edit.editing ? 'bg-[var(--ok)] text-white' : 'bg-[var(--accent)] text-[var(--accent-ink)] hover:brightness-110',
            )}
          >
            {edit.editing ? <Check className="size-5" strokeWidth={3} aria-hidden /> : <PenLine className="size-5" aria-hidden />}
            {edit.editing ? t('annotate.done') : t('annotate.write')}
          </button>
        ) : null}
        {reading?.shownOver ? (
          <>
            <button
              type="button"
              onClick={reading.onPin}
              aria-label={t('viewer.bars_pin')}
              title={t('viewer.bars_pin_hint')}
              className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-[var(--ink-2)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]"
            >
              <Pin className="size-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={reading.onClose}
              aria-label={t('viewer.bars_close')}
              title={t('viewer.bars_close')}
              className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)] text-[var(--ink-2)] hover:bg-[var(--accent-soft)]"
            >
              <ChevronUp className="size-6" aria-hidden />
            </button>
          </>
        ) : null}
      </div>
      {setlist ? (
        <div className="px-3 pb-2 lg:hidden">
          <SetlistNav nav={setlist} wide onShowList={onShowList} />
        </div>
      ) : null}
    </header>
  );
}
